import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import { redux } from 'Core/redux';
import { OverheadCraneService } from 'Service/api/overhead-crane-service';
import { showToast } from 'Core/redux/toast/slice';

export const STORE_KEY = 'overheadCrane';
export const INITIAL_STATE = {
  data: [],
  loading: false,
  error: null,
  filters: {
    searchText: '',
  },
  selectedOverheadCrane: null,
  tileView: true,
  maintenanceRecords: [],
  selectedMaintenanceRecord: null,
  maintenanceData: null,
  cellHistoryData: [],
};

// Async thunks
export const fetchOverheadCranes = createAsyncThunk(
  'overheadCrane/fetchOverheadCranes',
  async (_, { rejectWithValue }) => {
    try {
      const response = await OverheadCraneService.getAllOverheadCranes();
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load Overhead Cranes');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load Overhead Cranes');
    }
  },
);

export const addOverheadCrane = createAsyncThunk(
  'overheadCrane/addOverheadCrane',
  async (overheadCranePayload, { rejectWithValue }) => {
    try {
      const response = await OverheadCraneService.saveNewOverheadCrane(overheadCranePayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to add Overhead Crane');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add Overhead Crane');
    }
  },
);

export const editOverheadCrane = createAsyncThunk(
  'overheadCrane/editOverheadCrane',
  async (overheadCranePayload, { rejectWithValue }) => {
    try {
      const response = await OverheadCraneService.saveNewOverheadCrane(overheadCranePayload);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to update Overhead Crane');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update Overhead Crane');
    }
  },
);

export const fetchOverheadCranesWithFilters = () => (dispatch) => {
  dispatch(fetchOverheadCranes());
};

// Maintenance Records Thunks
export const fetchMaintenanceRecordsByOverheadCraneId = createAsyncThunk(
  'overheadCrane/fetchMaintenanceRecordsByOverheadCraneId',
  async (overheadCraneId, { rejectWithValue }) => {
    try {
      const response = await OverheadCraneService.getMaintenanceRecordsByOverheadCraneId(overheadCraneId);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load maintenance records');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load maintenance records');
    }
  },
);

export const fetchMaintenanceRecordsDetails = createAsyncThunk(
  'overheadCrane/fetchMaintenanceRecordsDetails',
  async (recordId, { rejectWithValue }) => {
    try {
      const response = await OverheadCraneService.getMaintenanceRecordDetails(recordId);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to load maintenance record details');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load maintenance record details');
    }
  },
);

// Fetch overhead crane by asset number for cell history
export const fetchOverheadCraneByAssetNumber = createAsyncThunk(
  'overheadCrane/fetchByAssetNumber',
  async (assetNumber) => {
    const { response } = await OverheadCraneService.getAllOverheadCraneByAssetNumber(assetNumber);
    return response;
  }
);

export const addOverheadCraneMaintenanceRecord = createAsyncThunk(
  'overheadCrane/addOverheadCraneMaintenanceRecord',
  async (maintenanceData, { rejectWithValue }) => {
    try {
      const response = await OverheadCraneService.addMaintenanceRecord(maintenanceData);
      if (response.status === 200) {
        return response.response;
      } else {
        return rejectWithValue(response.errorMessage?.message || 'Failed to add maintenance record');
      }
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to add maintenance record');
    }
  },
);

export const { reducer, actions, caseReducers } = createSlice({
  name: 'overheadCrane',
  initialState: INITIAL_STATE,
  reducers: {
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    resetFilters: state => {
      state.filters = INITIAL_STATE.filters;
    },
    setOverheadCrane: (state, action) => {
      state.selectedOverheadCrane = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setTileView: (state, action) => {
      state.tileView = action.payload;
    },
    setMaintenanceData: (state, action) => {
      state.maintenanceData = action.payload;
    },
    clearMaintenanceData: (state) => {
      state.maintenanceData = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchOverheadCranes.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOverheadCranes.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchOverheadCranes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addOverheadCrane.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addOverheadCrane.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          const newItem = {
            ...action.payload,
            isNewlyCreated: true,
          };
          state.data = [newItem, ...state.data];
        }
      })
      .addCase(addOverheadCrane.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(editOverheadCrane.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editOverheadCrane.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.data.findIndex(s => s.id === action.payload.id);
        if (index !== -1) {
          state.data[index] = action.payload;
        }
      })
      .addCase(editOverheadCrane.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Maintenance Records
      .addCase(fetchMaintenanceRecordsByOverheadCraneId.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRecordsByOverheadCraneId.fulfilled, (state, action) => {
        state.loading = false;
        state.maintenanceRecords = action.payload;
      })
      .addCase(fetchMaintenanceRecordsByOverheadCraneId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchMaintenanceRecordsDetails.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMaintenanceRecordsDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedMaintenanceRecord = action.payload;
      })
      .addCase(fetchMaintenanceRecordsDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addOverheadCraneMaintenanceRecord.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addOverheadCraneMaintenanceRecord.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(addOverheadCraneMaintenanceRecord.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch overhead crane by asset number (cell history)
      .addCase(fetchOverheadCraneByAssetNumber.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOverheadCraneByAssetNumber.fulfilled, (state, action) => {
        state.loading = false;
        state.cellHistoryData = action.payload;
      })
      .addCase(fetchOverheadCraneByAssetNumber.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message;
      });
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addOverheadCrane.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(fetchOverheadCranesWithFilters());
    listenerApi.dispatch(
      showToast({
        message: 'Overhead Crane saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addOverheadCrane.rejected, editOverheadCrane.rejected),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload,
        type: 'danger',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(editOverheadCrane.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: 'Overhead Crane updated successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addOverheadCraneMaintenanceRecord.fulfilled),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: 'Maintenance record saved successfully',
        type: 'success',
      }),
    );
  },
});

redux.listener.startListening({
  matcher: isAnyOf(addOverheadCraneMaintenanceRecord.rejected),
  effect: (action, listenerApi) => {
    listenerApi.dispatch(
      showToast({
        message: action.payload || 'Failed to save maintenance record',
        type: 'danger',
      }),
    );
  },
});

redux.injectReducer(STORE_KEY, reducer);

// Export individual actions
export const { 
  setOverheadCrane, 
  setMaintenanceData, 
  clearMaintenanceData, 
  resetFilters, 
  setTileView 
} = actions;
